<template>
  <article v-if="series && Object.keys(series).length" class="space-0-above">
    <PostFeaturedImage
      v-if="series.featuredImage"
      :featured-image="series.featuredImage"
      :srcset="series.imageSrcset"
      :is-wide="true"
    ></PostFeaturedImage>
    <nav>
      <BackLink
        component="CalendarSeries"
        group="calendar"
        label="Calendar"
        name="CalendarSection"
      ></BackLink>
    </nav>
    <section>
      <div :class="$style.content">
        <h1 v-html="series.name"></h1>
      </div>
      <ProcessContent
        v-if="series.seriesDescription"
        :class="$style.content"
        :content="series.seriesDescription"
        wrapper="section"
      ></ProcessContent>
      <div :class="$style.content">
        <CalendarMonth
          v-if="series.eventOccurrences.nodes.length"
          :events="series.eventOccurrences.nodes"
          :first-date="new Date(`${appConfig.today.isoDate}T00:00:00`)"
          :show-desc="true"
        ></CalendarMonth>
      </div>
    </section>
  </article>
  <BackToTop></BackToTop>
</template>

<script setup lang="ts">
import { AppConfig } from '@/types/global.interface';
import BackLink from '@/components/common/BackLink.vue';
import BackToTop from '@/components/common/BackToTop.vue';
import CalendarMonth from '@/components/calendar/CalendarMonth.vue';
import { calendarSeriesQuery } from '@/queries/GetCalendarSeries';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import { inject, ref } from 'vue';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { SeriesInterface } from '@/types/event.interface';
import { useQuery } from 'villus';

const appConfig = inject<AppConfig>('appConfig');

const props = defineProps<{
  slug: string;
}>();

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const series = ref<SeriesInterface>({});

const { data } = useQuery({
  query: calendarSeriesQuery,
  variables: { slug: props.slug },
  onData: data => {
    if (data.eventSerieses.nodes.length) {
      series.value = data.eventSerieses.nodes[0];
      setMetaTitle(data.eventSerieses.nodes[0].name);
      setMetaDescription(data.eventSerieses.nodes[0].description);
      dataLoaded();
    }

    if (data.eventSerieses.nodes.length === 0) {
      handleError('CalendarSeries', props.slug);
    }
  },
});
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: calendarSeriesQuery,
          variables: {
            slug: params.slug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss">
@use '@/styles/links.module.scss' as *;
</style>

<style lang="scss" module>
.content {
  p:first-of-type {
    margin-top: 0;
  }
}

@media (min-width: $break-point) {
  .content {
    @include grid-content;
  }
}
</style>
